import React, { useState, useEffect, useRef } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ShoppingBag, ChevronDown } from "lucide-react";
import NewsletterForm from "@/components/NewsletterForm";
import { useSettings } from "@/contexts/SettingsContext";
import { useCart } from "@/contexts/CartContext";
import api from "@/lib/upr-api";

const NAV = [
  { to: "/", label: "Music" },
  { to: "/artists", label: "Artists" },
  { to: "/merch", label: "Merch" },
  { to: "/video", label: "Video" },
  { to: "/blog", label: "Blog" },
  { to: "/playlists", label: "Playlists" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { settings } = useSettings();
  const { count, setOpen: setCartOpen } = useCart();
  const logoUrl = settings?.logo_url;
  const useDefault = settings?.use_default_logo !== false;
  const logoText = (settings?.logo_text || "UNIVERSO POSITIVO").toUpperCase();
  const logoTagline = settings?.logo_tagline || "Records · Est. since the sun";
  const siteTitle = settings?.site_title || "UNIVERSO POSITIVO";
  const logoHeightPx = Math.max(24, Math.min(160, Number(settings?.logo_height_px) || 40));
  const logoIconUrl = settings?.logo_icon_url || "";
  const logoIconSizePx = Math.max(24, Math.min(96, Number(settings?.logo_icon_size_px) || 32));

  // Called from any footer link — instantly scroll the new page to the top so
  // the user doesn't stay stuck at the bottom of the previous page.
  const scrollTop = () => window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });

  // Auto-close mobile drawer whenever the route changes. Also lock body scroll
  // while the mobile drawer is open.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  // Reset scroll to the top on every route change, no matter which link
  // triggered it (header nav, mobile drawer, footer, or logo).
  useEffect(() => {
    scrollTop();
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  return (
    <div className="min-h-screen flex flex-col" data-testid="upr-layout">
      <header
        className="sticky top-0 z-50 backdrop-blur"
        style={{ background: "rgba(250,249,246,0.92)", borderBottom: "1px solid var(--upr-border)" }}
        data-testid="site-header"
      >
        <div className="px-6 md:px-12 lg:px-16 py-4 flex items-center justify-between gap-4 min-h-[64px]">
          <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="logo-link" onClick={() => setOpen(false)}>
            {logoUrl && !useDefault ? (
              <img
                src={logoUrl}
                alt={siteTitle}
                className="w-auto object-contain"
                style={{ height: `${logoHeightPx}px`, maxWidth: "320px" }}
                data-testid="site-logo-image"
              />
            ) : (
              <>
                {logoIconUrl ? (
                  <img
                    src={logoIconUrl}
                    alt=""
                    className="rounded-full object-cover shrink-0"
                    style={{ width: `${logoIconSizePx}px`, height: `${logoIconSizePx}px` }}
                    data-testid="site-logo-icon-image"
                    aria-hidden
                  />
                ) : (
                  <span className="inline-block w-8 h-8 rounded-full upr-vinyl-spin relative" style={{ background: "radial-gradient(circle, #1C1B1A 0 35%, var(--upr-primary) 36% 38%, #1C1B1A 39% 100%)", boxShadow: "inset 0 0 0 1px #1C1B1A" }} aria-hidden />
                )}
                <div className="leading-none">
                  <div className="font-heading font-black tracking-tight text-lg" style={{ letterSpacing: "-0.02em" }} data-testid="site-logo-text">
                    {logoText}
                  </div>
                  <div className="upr-eyebrow" style={{ fontSize: "0.6rem" }} data-testid="site-logo-tagline">{logoTagline}</div>
                </div>
              </>
            )}
          </Link>

          <nav className="hidden md:flex items-center gap-7 lg:gap-8" data-testid="primary-nav">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  "upr-link-underline font-heading font-extrabold uppercase tracking-widest text-[0.78rem] transition-colors " +
                  (isActive ? "text-[color:var(--upr-primary)]" : "text-[color:var(--upr-fg)] hover:text-[color:var(--upr-primary)]")
                }
              >
                {n.label}
              </NavLink>
            ))}

            <NavLink
              to="/contact"
              data-testid="nav-contact"
              className={({ isActive }) =>
                "upr-link-underline font-heading font-extrabold uppercase tracking-widest text-[0.78rem] transition-colors " +
                (isActive ? "text-[color:var(--upr-primary)]" : "text-[color:var(--upr-fg)] hover:text-[color:var(--upr-primary)]")
              }
            >
              Contact
            </NavLink>

            {/* Tags — simple link to the Tags page (matches the footer link). */}
            <NavLink
              to="/tags"
              data-testid="nav-tags"
              className={({ isActive }) =>
                "upr-link-underline font-heading font-extrabold uppercase tracking-widest text-[0.78rem] transition-colors " +
                (isActive ? "text-[color:var(--upr-primary)]" : "text-[color:var(--upr-fg)] hover:text-[color:var(--upr-primary)]")
              }
            >
              Tags
            </NavLink>
          </nav>

          <button
            type="button"
            className="hidden md:inline-flex items-center gap-2 ml-4 px-3 py-2 font-heading uppercase tracking-widest text-[0.7rem] transition-colors relative"
            style={{ border: "1px solid var(--upr-border)" }}
            onClick={() => setCartOpen(true)}
            aria-label="Open cart"
            data-testid="header-cart-button"
          >
            <ShoppingBag size={14} />
            <span>Cart</span>
            {count > 0 && (
              <span
                className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1 inline-flex items-center justify-center font-heading text-[0.65rem] rounded-full"
                style={{ background: "var(--upr-primary)", color: "var(--upr-bg)" }}
                data-testid="cart-count"
              >
                {count}
              </span>
            )}
          </button>

          <button
            type="button"
            className="md:hidden p-2 relative"
            onClick={() => setCartOpen(true)}
            aria-label="Open cart"
            data-testid="mobile-cart-button"
          >
            <ShoppingBag size={20} />
            {count > 0 && (
              <span
                className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 inline-flex items-center justify-center font-heading text-[0.55rem] rounded-full"
                style={{ background: "var(--upr-primary)", color: "var(--upr-bg)" }}
              >
                {count}
              </span>
            )}
          </button>

          <button
            type="button"
            className="md:hidden p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <>
            {/* Full-page backdrop closes the drawer on tap outside */}
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setOpen(false)}
              className="fixed inset-0 top-[64px] md:hidden z-40"
              style={{ background: "rgba(28,27,26,0.28)", backdropFilter: "blur(2px)" }}
              data-testid="mobile-nav-backdrop"
            />
            <div className="md:hidden border-t relative z-50" style={{ borderColor: "var(--upr-border)", background: "var(--upr-bg)" }} data-testid="mobile-nav">
              <nav className="px-6 py-4 flex flex-col divide-y" style={{ borderColor: "var(--upr-border)" }}>
                {NAV.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.to === "/"}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      "font-heading font-extrabold uppercase tracking-widest text-sm py-3 min-h-[44px] flex items-center " +
                      (isActive ? "text-[color:var(--upr-primary)]" : "text-[color:var(--upr-fg)]")
                    }
                    style={{ borderColor: "var(--upr-border)" }}
                  >
                    {n.label}
                  </NavLink>
                ))}
                <NavLink
                  to="/contact"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-contact"
                  className={({ isActive }) =>
                    "font-heading font-extrabold uppercase tracking-widest text-sm py-3 min-h-[44px] flex items-center " +
                    (isActive ? "text-[color:var(--upr-primary)]" : "text-[color:var(--upr-fg)]")
                  }
                >
                  Contact
                </NavLink>
              </nav>
            </div>
          </>
        )}
      </header>

      <main className="flex-1" key={location.pathname}>{children}</main>

      <footer
        className="mt-24 border-t"
        style={{ borderColor: "var(--upr-border)", background: "var(--upr-surface)" }}
        data-testid="site-footer"
      >
        <div className="px-6 md:px-12 lg:px-16 py-12 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            {logoUrl && !useDefault ? (
              <img src={logoUrl} alt={siteTitle} className="w-auto object-contain" style={{ height: `${Math.round(logoHeightPx * 1.2)}px`, maxWidth: "320px" }} data-testid="footer-logo-image" />
            ) : (
              <div className="font-heading font-black text-2xl tracking-tight leading-none" data-testid="footer-logo-text">
                {logoText.split(" ").map((w, i) => (<span key={i}>{w}{i < logoText.split(" ").length - 1 ? <br/> : null}</span>))}
              </div>
            )}
            <p className="mt-4 text-sm" style={{ color: "var(--upr-muted)" }} data-testid="footer-about">
              {settings?.footer_about || "Independent record label for soulful, sun-soaked, hand-played music. Lisbon ✕ Amsterdam."}
            </p>
            {settings?.footer_tagline && (
              <p className="mt-2 upr-eyebrow" data-testid="footer-tagline">{settings.footer_tagline}</p>
            )}
          </div>
          <div className="md:col-span-2">
            <div className="upr-eyebrow mb-3">Explore</div>
            <ul className="space-y-2 text-sm">
              {NAV.map((n) => (
                <li key={n.to}><Link to={n.to} onClick={scrollTop} className="upr-link-underline" data-testid={`footer-link-${n.label.toLowerCase()}`}>{n.label}</Link></li>
              ))}
              <li><Link to="/contact" onClick={scrollTop} className="upr-link-underline" data-testid="footer-link-contact">Contact</Link></li>
              <li><Link to="/tags" onClick={scrollTop} className="upr-link-underline" data-testid="footer-link-tags">Tags</Link></li>
            </ul>
          </div>
          <div className="md:col-span-2">
            <div className="upr-eyebrow mb-3">Off-platform</div>
            <ul className="space-y-2 text-sm">
              <li><a href="https://universopositivo.bandcamp.com/" target="_blank" rel="noreferrer" className="upr-link-underline">Bandcamp</a></li>
              <li><Link to="/contact" className="upr-link-underline">Demos & Press</Link></li>
              <li><Link to="/my-orders" className="upr-link-underline" data-testid="footer-my-orders-link">Find my downloads</Link></li>
              <li><Link to="/admin" className="upr-link-underline" data-testid="footer-admin-link">Admin</Link></li>
            </ul>
          </div>
          <div className="md:col-span-4">
            <div className="upr-eyebrow mb-3">Letters from the studio</div>
            <p className="text-sm mb-3" style={{ color: "var(--upr-muted)" }}>
              Release announcements, label nights, the occasional download. Once a month, max.
            </p>
            <NewsletterForm source="footer" testid="footer-newsletter" />
          </div>
        </div>
        <div className="px-6 md:px-12 lg:px-16 py-5 border-t flex flex-col md:flex-row justify-between gap-2 text-xs" style={{ borderColor: "var(--upr-border)", color: "var(--upr-muted)" }}>
          <div data-testid="footer-copyright">
            {(settings?.footer_copyright || "© {year} Universo Positivo Records. All rights reserved.")
              .replace("{year}", String(new Date().getFullYear()))}
          </div>
          <div data-testid="footer-url">{settings?.footer_url || "www.universopositivorecords.com"}</div>
        </div>
      </footer>
    </div>
  );
}
